/* eslint-disable max-len */
/* eslint-disable react/no-danger */
/* eslint-disable no-console */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Box } from '@mui/material';
import ReadDescriptionButton from '../../LeftPart/FeedCards/Card/ReadDescriptionButton';
import ReactDOM from 'react-dom';
import { feedActionContext } from '../../..';
import './styles.css';

interface ReadMoreLessProps {
	text: string;
	maxChars?: number;
	fontSize?: string;
	fontWeight?: number;
	textColor?: string;
	buttonColor?: string;
	isHighlighted?: boolean;
	feedId?: string;
	showSpeaker?: boolean;

}

const ReadMoreLess: React.FC<ReadMoreLessProps> = ({
	text = '',
	maxChars = 100,
	fontSize = 'var(--pixel16)',
	fontWeight = 400,
	textColor = 'black',
	buttonColor = 'blue',
	feedId = '',
	showSpeaker = true
}) =>
{
	const [ isExpanded, setIsExpanded ] = useState(false);
	const descriptionRef = useRef<HTMLDivElement>(null);
	const [ desc, setDesc ] = useState({ text: '', count: 0, height: 0 });
	const [ isReading, setIsReading ] = useState(false);
	const { feedAction } = useContext(feedActionContext);

	const toggleReadMore = () =>
	{
		setIsExpanded(!isExpanded);
		if (isExpanded && feedId)
		{ // scrolling element into view when clicking on read less
			const elem = document.getElementById(feedId);

			if (elem)
			{
				const dim = elem.getBoundingClientRect();

				!(dim.top >= 0 && dim.left >= 0) && elem.scrollIntoView();
			}
		}
	};

	function truncateHTML(html)
	{
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		let charCount = 0;
		let truncated = '';

		function processNode(node)
		{
			if (charCount >= maxChars) return;

			if (node.nodeType === Node.TEXT_NODE)
			{
				// Extract only needed characters
				// eslint-disable-next-line no-shadow
				let text = node.textContent;

				if (charCount + text.length > maxChars)
				{
					text = `${text.slice(0, maxChars - charCount)}...`;
				}
				charCount += text.length;
				truncated += text;
			}
			else if (node.nodeType === Node.ELEMENT_NODE)
			{
				const tagName = node.tagName.toLowerCase();

				// List of void/self-closing elements that don't need closing tags
				const voidElements = [ 'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr' ];
				const isVoidElement = voidElements.includes(tagName);

				// Open tag
				truncated += `<${tagName}${node.attributes.length ? ' ' : ''}`;

				// Preserve attributes
				for (const attr of node.attributes)
				{
					truncated += `${attr.name}="${attr.value}" `;
				}
				truncated = `${truncated.trim()}>`;

				// Process child nodes recursively (only if not a void element)
				if (!isVoidElement)
				{
					for (const child of node.childNodes)
					{
						processNode(child);
						if (charCount >= maxChars) break;
					}

					// Close tag properly (only for non-void elements)
					truncated += `</${tagName}>`;
				}
			}
		}

		for (const child of doc.body.childNodes)
		{
			processNode(child);
			if (charCount >= maxChars) break;
		}

		return truncated;
	}

	useEffect(() =>
	{
		if (descriptionRef.current && desc.text !== descriptionRef.current?.textContent)
		{
			const div = document.createElement('div');

			div.innerText = text;
			if (descriptionRef.current.parentNode)
			{
				descriptionRef.current.parentNode.insertBefore(div, descriptionRef.current.nextSibling);
			}

			const parser = new DOMParser();
			const doc = parser.parseFromString(text ?? '', 'text/html');

			const textContent = div.textContent || '';
			const contentElement:any = div;

			const lineHeight = parseFloat(getComputedStyle(contentElement).lineHeight);
			const elementHeight = contentElement.clientHeight;
			const lines = Math.floor(elementHeight / lineHeight);

			setDesc({ text: textContent, count: doc.body.textContent?.length || textContent?.length, height: lines });
			if (div && div.parentNode)
			{
				div.parentNode.removeChild(div);
			}
		}
	}, [ text ]);

	const maxHeight = 7;
	const isLongText = desc?.count > maxChars || desc?.height > maxHeight;
	const feedDescription =	isReading || isExpanded || !isLongText ? text : truncateHTML(text);
	const descStyle = { overflow   : 'hidden',
		lineHeight : '1.5em',
		maxHeight  : `calc(1.5em * ${maxHeight})`,
		a          : {
			wordBreak : 'everywhere'
		} };

	const readMoreElem =
	feedAction?.is_full_screen
		? document.getElementById(`feed_description_button_full_screen_${feedId}`)
		: document.getElementById(`feed_description_button_${feedId}`);

	const descWithoutEmoji = useMemo(() =>
	{
		const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
		const newLineRegex = /<\/p><p>/g;
		const descText = (text || '')
			.replace(emojiRegex, '')
			.replace(newLineRegex, ' ');
		const parser = new DOMParser();

		return parser.parseFromString(descText, 'text/html').body.textContent?.trim() || '';
	}, [ text ]);

	function modifyPTags(htmlString, maxIndex)
	{
		return htmlString.replace(/<p>(.*?)<\/p>/g, (match, content, offset) =>
		{
			let currentIndex = offset;

			if (offset + match.length < maxIndex)
			{
				return `<p style='background:#FBFBD4'>${content}</p>`;
			}
			else if (offset > maxIndex)	return match;

			let flag = false;
			const modifiedContent = content.split(' ').reduce((acc, word) =>
			{
				if (currentIndex <= maxIndex)
				{
					currentIndex += word.length + 1;

					return `${acc + word} `;
				}
				else
				{
					currentIndex += word.length + 1;

					if (flag) return ` ${acc + word} `;
					else
					{
						flag = true;

						return `${acc}</span> ${word} `;
					}
				}
			}, '<span style="background:#FBFBD4">');

			return `<p>${modifiedContent}</p>`;
		});
	}

	const maxIndexReached = useRef(0);
	const prevIndex = useRef(-1);
	const getSpeechInfo = (event) =>
	{
		!isReading && setIsReading(true);
		if (!descriptionRef.current) return;
		const currentWord = descWithoutEmoji.substring(event.charIndex, event.charIndex + event.charLength);

		if (event.charIndex !== prevIndex.current)
		{
			const matchIndex = text.indexOf(currentWord, maxIndexReached.current);

			prevIndex.current = event.charIndex;
			maxIndexReached.current = matchIndex + 1;

			if (matchIndex === -1) return;

			const newDesc = modifyPTags(text, matchIndex);

			descriptionRef.current.innerHTML = newDesc;
		}
	};

	const speechEndCallback = () =>
	{
		if (descriptionRef.current)
		{
			descriptionRef.current.innerHTML = text;
			prevIndex.current = -1;
			maxIndexReached.current = 0;
			setIsReading(false);
		}
	};

	const SpeakerButton = useMemo(() =>
	{
		return readMoreElem && readMoreElem?.childElementCount === 0 && ReactDOM.createPortal(
			<ReadDescriptionButton
				feed={{ description: text, _id: feedId }}
				getSpeechInfo={getSpeechInfo}
				speechEndCallback={speechEndCallback}
			/>,
			readMoreElem,
			feedId
		);
	}, [ feedId, readMoreElem ]);

	return (
		<Box>
			<Typography
				component='span'
				sx={{
					fontSize,
					fontWeight,
					color   : textColor,
					display : 'inline'
				}}
			>
				<span
					className={'feed_description'}
					ref={descriptionRef}
					style={desc.height > maxHeight && !isExpanded && !isReading ? descStyle : { lineHeight: '1.5em' }}
					dangerouslySetInnerHTML={{ __html: feedDescription }}
				/>
				{!isReading && isLongText &&
				<Typography
					onClick={toggleReadMore}
					sx={{
						color         : buttonColor,
						fontSize,
						textTransform : 'none',
						padding       : 0,
						minWidth      : 'unset',
						marginLeft    : '8px',
						display       : 'inline-block',
						cursor        : 'pointer'
					}}
				>
					{isExpanded
						? 'Read less'
						: 'Read more'}
				</Typography>}
			</Typography>
			{showSpeaker && readMoreElem && SpeakerButton}
		</Box>
	);
};

export default ReadMoreLess;